import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage, 
  faFont, 
  faShapes, 
  faUpload,
  faTrash,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { 
  ImageAdjustments, 
  TextElement, 
  ShapeElement,
  AssetElement 
} from '../../../types/thumbnailTypes';
import TextTemplates from './TextTemplates';
import { uploadThumbnailAsset, getUserAssets, uploadUserAsset, deleteUserAsset, UserAsset } from '../../../services/thumbnailCompositionApi';
import TextCapitalizationButtons from '../../shared/TextCapitalizationButtons';
import TextAlignDropdown from '../../shared/TextAlignDropdown';
import styles from './ThumbnailRightPanel.module.css';

interface ThumbnailRightPanelProps {
  imageAdjustments: ImageAdjustments;
  onUpdateImageAdjustments: (adjustments: Partial<ImageAdjustments>) => void;
  textElements: TextElement[];
  onAddTextElement: (text: string, position: { x: number; y: number }, customStyle?: Partial<TextElement['style']>) => void;
  onUpdateTextElement: (id: string, updates: Partial<TextElement>) => void;
  onRemoveTextElement: (id: string) => void;
  shapeElements: ShapeElement[];
  onAddShapeElement: (type: 'rectangle' | 'circle', position: { x: number; y: number }) => void;
  onUpdateShapeElement: (id: string, updates: Partial<ShapeElement>) => void;
  onRemoveShapeElement: (id: string) => void;
  assetElements: AssetElement[];
  onAddAssetElement: (asset: AssetElement) => void;
  onUpdateAssetElement: (id: string, updates: Partial<AssetElement>) => void;
  onRemoveAssetElement: (id: string) => void;
  selectedElement: string | null;
  projectId?: string;
  userId?: string; // Add userId prop
  initialTab?: 'image' | 'text' | 'shapes' | 'assets'; // New prop for setting the active tab
  onTabChange?: (tab: 'image' | 'text' | 'shapes' | 'assets') => void; // New callback prop
}

const ThumbnailRightPanel: React.FC<ThumbnailRightPanelProps> = ({
  imageAdjustments,
  onUpdateImageAdjustments,
  textElements,
  onAddTextElement,
  onUpdateTextElement,
  onRemoveTextElement,
  shapeElements,
  onAddShapeElement,
  onUpdateShapeElement,
  onRemoveShapeElement,
  assetElements,
  onAddAssetElement,
  onUpdateAssetElement,
  onRemoveAssetElement,
  selectedElement,
  projectId,
  userId, // Add userId parameter
  initialTab,
  onTabChange
}) => {
  const { t } = useTranslation('thumbnailEditor');
  const [activeTab, setActiveTab] = useState<'image' | 'text' | 'shapes' | 'assets'>(initialTab || 'image');
  const [newText, setNewText] = useState(t('thumbnailEditor.textTab.placeholder'));
  const [userAssets, setUserAssets] = useState<UserAsset[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Update activeTab when initialTab prop changes (for external control)
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
      
      // Scroll to selected element section when tab changes and there's a selected element
      setTimeout(() => {
        scrollToSelectedElement();
      }, 100); // Small delay to ensure tab content is rendered
    }
  }, [initialTab]);

  // Scroll to selected element section when a new element is selected
  useEffect(() => {
    if (selectedElement) {
      setTimeout(() => {
        scrollToSelectedElement();
      }, 150); // Slightly longer delay to ensure element is rendered
    }
  }, [selectedElement]);

  // Function to scroll to the selected element section
  const scrollToSelectedElement = () => {
    const selectedElementSection = document.querySelector(`.${styles.selectedElement}`);
    if (selectedElementSection) {
      selectedElementSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  // Handle tab change with callback
  const handleTabChange = (tab: 'image' | 'text' | 'shapes' | 'assets') => {
    setActiveTab(tab);
    onTabChange?.(tab);
    
    // Scroll to selected element section when manually changing tabs
    setTimeout(() => {
      scrollToSelectedElement();
    }, 100);
  };

  const availableFonts = [
    'Bebas Neue',
    'Anton',
    'Lilita One',
    'Alfa Slab One',
    'Rubik Mono One',
    'Passion One',
    'Concert One',
    'Luckiest Guy',
    'Titan One',
    'Paytone One',
    'Bangers',
    'Geologica',
    'Atma',
    'Chewy',
    'Ranchers',
    'Arial'
  ];

  const selectedTextElement = textElements.find(el => el.id === selectedElement);
  const selectedShapeElement = shapeElements.find(el => el.id === selectedElement);

  // Load user assets when component mounts or userId changes
  useEffect(() => {
    // Only load if we have a valid userId string
    if (userId && typeof userId === 'string') {
      loadUserAssets(userId);
    } else if (!userId) {
      // For testing purposes, use a hardcoded userId if none provided
      loadUserAssets('test-user-123');
    }
  }, [userId]);

  const loadUserAssets = async (effectiveUserId: string) => {
    if (!effectiveUserId || typeof effectiveUserId !== 'string') {
      console.warn('Invalid userId provided to loadUserAssets:', effectiveUserId);
      return;
    }
    
    setIsLoadingAssets(true);
    try {
      const assets = await getUserAssets(effectiveUserId);
      setUserAssets(assets);
    } catch (error) {
      console.error('Failed to load user assets:', error);
      // Don't show alert for initial load failures
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const handleAddText = () => {
    // Default textAlign is 'center', so position.x should be at canvas center (320 for 640px canvas)
    onAddTextElement(newText, { x: 320, y: 200 });
  };

  const handleAddShape = (type: 'rectangle' | 'circle') => {
    onAddShapeElement(type, { x: 300, y: 150 });
  };

  const handleAssetUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    // Determine effective userId
    let effectiveUserId: string;
    if (userId && typeof userId === 'string') {
      effectiveUserId = userId;
    } else {
      effectiveUserId = 'test-user-123';
    }

    setIsUploading(true);

    try {
      for (const file of Array.from(files)) {
        // Check if file is an image
        if (!file.type.startsWith('image/')) {
          alert('Please upload only image files');
          continue;
        }

        // Check file size (max 10MB to match backend)
        if (file.size > 10 * 1024 * 1024) {
          alert('File size must be less than 10MB');
          continue;
        }

        // Upload to user account (not project-specific)
        const result = await uploadUserAsset(effectiveUserId, file, file.name, 'other');
      }
      
      // Reload user assets to get the updated list after all uploads
      await loadUserAssets(effectiveUserId);

    } catch (error) {
      console.error('Asset upload failed:', error);
      alert(`Failed to upload: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      // Reset the input
      event.target.value = '';
    }
  };

  const handleAddAssetToCanvas = (asset: UserAsset) => {
    // Generate a unique ID for this canvas instance
    const uniqueCanvasId = `canvas_${asset.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Add a small random offset so multiple instances don't stack exactly
    const offsetX = Math.random() * 50 - 25; // Random offset between -25 and +25
    const offsetY = Math.random() * 50 - 25;
    
    // Add asset element to canvas
    const newAssetElement: AssetElement = {
      id: uniqueCanvasId, // Use unique ID for each canvas instance
      assetUrl: asset.assetUrl,
      position: { x: 250 + offsetX, y: 150 + offsetY },
      dimensions: { 
        width: asset.dimensions?.width ? Math.min(asset.dimensions.width, 200) : 150, 
        height: asset.dimensions?.height ? Math.min(asset.dimensions.height, 200) : 150 
      },
      opacity: 100,
      rotation: 0
    };

    onAddAssetElement(newAssetElement);
  };

  const handleRemoveAsset = async (assetId: string) => {
    // Determine effective userId
    let effectiveUserId: string;
    if (userId && typeof userId === 'string') {
      effectiveUserId = userId;
    } else {
      effectiveUserId = 'test-user-123';
    }
    
    try {
      // First, find the asset to get its URL
      const assetToDelete = userAssets.find(asset => asset.id === assetId);
      if (!assetToDelete) return;

      // Remove all canvas instances of this asset
      const instancesToRemove = assetElements.filter(element => 
        element.assetUrl === assetToDelete.assetUrl
      );
      
      // Remove each instance from canvas
      instancesToRemove.forEach(instance => {
        onRemoveAssetElement(instance.id);
      });

      // Delete from user's asset library
      await deleteUserAsset(effectiveUserId, assetId);
      
      // Reload assets after deletion
      await loadUserAssets(effectiveUserId);
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset');
    }
  };

  const renderImageTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.control}>
        <label>{t('thumbnailEditor.adjustments.contrast')}</label>
        <div className={styles.sliderContainer}>
          <input
            type="range"
            min="-100"
            max="100"
            step="1"
            value={imageAdjustments.contrast || 0}
            onChange={(e) => onUpdateImageAdjustments({ contrast: parseInt(e.target.value) })}
            className={styles.customSlider}
          />
          <div className={styles.sliderLabels}>
            <span>-100</span>
            <span>0</span>
            <span>100</span>
          </div>
        </div>
      </div>

      <div className={styles.control}>
        <label>{t('thumbnailEditor.adjustments.brightness')}</label>
        <div className={styles.sliderContainer}>
          <input
            type="range"
            min="-100"
            max="100"
            step="1"
            value={imageAdjustments.brightness || 0}
            onChange={(e) => onUpdateImageAdjustments({ brightness: parseInt(e.target.value) })}
            className={styles.customSlider}
          />
          <div className={styles.sliderLabels}>
            <span>-100</span>
            <span>0</span>
            <span>100</span>
          </div>
        </div>
      </div>

      <div className={styles.switchContainer}>
        <Switch
          label={t("thumbnailEditor.adjustments.blackAndWhite")}
          checked={imageAdjustments.blackAndWhite || false}
          onChange={(event) => onUpdateImageAdjustments({ blackAndWhite: event.currentTarget.checked })}
        />
      </div>

      <div className={styles.sectionTitle}>{t('thumbnailEditor.adjustments.positionScale')}</div>
      
      <div className={styles.controlRow}>
        <div className={styles.control}>
          <label>{t('thumbnailEditor.adjustments.xPosition')}</label>
          <input
            type="number"
            value={imageAdjustments.position?.x || 0}
            onChange={(e) => onUpdateImageAdjustments({ 
              position: { ...imageAdjustments.position, x: parseInt(e.target.value) || 0 }
            })}
            className={styles.numberInput}
          />
        </div>
        <div className={styles.control}>
          <label>{t('thumbnailEditor.adjustments.yPosition')}</label>
          <input
            type="number"
            value={imageAdjustments.position?.y || 0}
            onChange={(e) => onUpdateImageAdjustments({ 
              position: { ...imageAdjustments.position, y: parseInt(e.target.value) || 0 }
            })}
            className={styles.numberInput}
          />
        </div>
      </div>

      <div className={styles.controlRow}>
        <div className={styles.control}>
          <label>{t('thumbnailEditor.adjustments.scaleX')}</label>
          <input
            type="number"
            min="0.1"
            max="3"
            step="0.1"
            value={imageAdjustments.scale?.scaleX || 1}
            onChange={(e) => {
              const newScaleX = parseFloat(e.target.value) || 1;
              console.log('ScaleX changed to:', newScaleX);
              onUpdateImageAdjustments({ 
                scale: { ...(imageAdjustments.scale || { scaleX: 1, scaleY: 1 }), scaleX: newScaleX }
              });
            }}
            className={styles.numberInput}
          />
        </div>
        <div className={styles.control}>
          <label>{t('thumbnailEditor.adjustments.scaleY')}</label>
          <input
            type="number"
            min="0.1"
            max="3"
            step="0.1"
            value={imageAdjustments.scale?.scaleY || 1}
            onChange={(e) => {
              const newScaleY = parseFloat(e.target.value) || 1;
              console.log('ScaleY changed to:', newScaleY);
              onUpdateImageAdjustments({ 
                scale: { ...(imageAdjustments.scale || { scaleX: 1, scaleY: 1 }), scaleY: newScaleY }
              });
            }}
            className={styles.numberInput}
          />
        </div>
      </div>
    </div>
  );  const renderTextTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.control}>
        <label>{t('thumbnailEditor.textTab.addNewContent')}</label>
        <textarea
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder={t('thumbnailEditor.textTab.placeholder')}
          rows={2}
          className={styles.textareaInput}
        />
      </div>
      
      <div className={styles.buttonSection}>
        <button onClick={handleAddText} className={`${styles.actionButton} ${styles.primary}`}>
          <FontAwesomeIcon icon={faPlus} />
          {t('thumbnailEditor.textTab.addButton')}
        </button>
      </div>

      {selectedTextElement && (
        <div className={styles.selectedElement}>
          <div className={styles.sectionTitle}>{t('thumbnailEditor.textTab.selectedText')}</div>
          
          <div className={styles.control}>
            <label>{t('thumbnailEditor.textTab.textContent')}</label>
            <textarea
              key={`text-content-${selectedTextElement.id}`}
              value={selectedTextElement.text}
              onChange={(e) => onUpdateTextElement(selectedTextElement.id, { text: e.target.value })}
              rows={2}
              className={styles.textareaInput}
              placeholder="Enter your text..."
            />
          </div>

          <div className={styles.control}>
            <label>{t('thumbnailEditor.textTab.textCapitalization')}</label>
            <TextCapitalizationButtons
              key={`text-cap-${selectedTextElement.id}-${selectedTextElement.text}`}
              currentText={selectedTextElement.text}
              onTextChange={(newText) => onUpdateTextElement(selectedTextElement.id, { text: newText })}
            />
          </div>

          <div className={styles.control}>
            <label>{t('thumbnailEditor.textTab.fontFamily')}</label>
            <div className={styles.fontSelector}>
              <select
                value={selectedTextElement.style.fontFamily}
                onChange={(e) => onUpdateTextElement(selectedTextElement.id, {
                  style: { ...selectedTextElement.style, fontFamily: e.target.value }
                })}
                className={styles.hiddenSelect}
              >
                {availableFonts.map(font => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
              <div className={styles.fontGrid}>
                {availableFonts.map(font => (
                  <button
                    key={font}
                    type="button"
                    className={`${styles.fontOption} ${selectedTextElement.style.fontFamily === font ? styles.selected : ''}`}
                    onClick={() => onUpdateTextElement(selectedTextElement.id, {
                      style: { ...selectedTextElement.style, fontFamily: font }
                    })}
                    style={{ fontFamily: font }}
                  >
                    {font}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.control}>
            <label>{t('thumbnailEditor.textTab.fontSize')}</label>
            <input
              type="number"
              value={selectedTextElement.style.fontSize}
              onChange={(e) => onUpdateTextElement(selectedTextElement.id, {
                style: { ...selectedTextElement.style, fontSize: Number(e.target.value) || 16 }
              })}
              min={8}
              max={200}
              step={1}
              className={styles.numberInput}
            />
          </div>

          <div className={styles.control}>
            <label>{t('thumbnailEditor.textTab.textColor')}</label>
            <div className={styles.colorInputContainer}>
              <input
                type="color"
                value={selectedTextElement.style.color}
                onChange={(e) => onUpdateTextElement(selectedTextElement.id, {
                  style: { ...selectedTextElement.style, color: e.target.value }
                })}
                className={styles.colorInput}
              />
              <input
                type="text"
                value={selectedTextElement.style.color}
                onChange={(e) => onUpdateTextElement(selectedTextElement.id, {
                  style: { ...selectedTextElement.style, color: e.target.value }
                })}
                className={styles.colorTextInput}
                placeholder="#FFFFFF"
              />
            </div>
          </div>

          <div className={styles.control} key={`text-align-container-${selectedTextElement.id}-${selectedTextElement.style.textAlign}`}>
            <TextAlignDropdown
              value={selectedTextElement.style.textAlign || 'center'}
              onChange={(newAlign) => {
                // Calculate new position based on alignment change
                // Assume canvas width of 640px for positioning
                let newX = selectedTextElement.position.x;
                
                if (newAlign === 'left') {
                  newX = 20; // Left edge with padding
                } else if (newAlign === 'center') {
                  newX = 320; // Center of canvas
                } else if (newAlign === 'right') {
                  newX = 620; // Right edge with padding (640 - 20)
                }
                
                // Update both style and position
                onUpdateTextElement(selectedTextElement.id, {
                  style: { ...selectedTextElement.style, textAlign: newAlign },
                  position: { ...selectedTextElement.position, x: newX }
                });
              }}
            />
          </div>

          <div className={styles.switchContainer}>
            <Switch
              label={t("thumbnailEditor.textTab.enableShadow")}
              checked={selectedTextElement.style.shadow?.enabled || false}
              onChange={(event) => onUpdateTextElement(selectedTextElement.id, {
                style: {
                  ...selectedTextElement.style,
                  shadow: {
                    ...selectedTextElement.style.shadow,
                    enabled: event.currentTarget.checked
                  }
                }
              })}
            />
          </div>

          <div className={styles.switchContainer}>
            <Switch
              label="Enable Border"
              checked={selectedTextElement.style.border?.enabled || false}
              onChange={(event) => {
                const isEnabled = event.currentTarget.checked;
                const newBorderState = isEnabled ? {
                  ...selectedTextElement.style.border,
                  enabled: true,
                  size: selectedTextElement.style.border?.size || 3,
                  color: selectedTextElement.style.border?.color || '#FFFFFF'
                } : {
                  enabled: false,
                  size: 0,
                  color: selectedTextElement.style.border?.color || '#FFFFFF'
                };
                
                onUpdateTextElement(selectedTextElement.id, {
                  style: {
                    ...selectedTextElement.style,
                    border: newBorderState
                  }
                });
              }}
            />
          </div>

          {selectedTextElement.style.border?.enabled && (
            <>
              <div className={styles.control}>
                <label>Border Size</label>
                <input
                  type="number"
                  value={selectedTextElement.style.border?.size || 3}
                  onChange={(e) => onUpdateTextElement(selectedTextElement.id, {
                    style: {
                      ...selectedTextElement.style,
                      border: {
                        ...selectedTextElement.style.border,
                        size: Number(e.target.value) || 1
                      }
                    }
                  })}
                  min={1}
                  max={20}
                  step={1}
                  className={styles.numberInput}
                />
              </div>

              <div className={styles.control}>
                <label>Border Color</label>
                <div className={styles.colorInputContainer}>
                  <input
                    type="color"
                    value={selectedTextElement.style.border?.color || '#FFFFFF'}
                    onChange={(e) => onUpdateTextElement(selectedTextElement.id, {
                      style: {
                        ...selectedTextElement.style,
                        border: {
                          ...selectedTextElement.style.border,
                          color: e.target.value
                        }
                      }
                    })}
                    className={styles.colorInput}
                  />
                  <input
                    type="text"
                    value={selectedTextElement.style.border?.color || '#FFFFFF'}
                    onChange={(e) => onUpdateTextElement(selectedTextElement.id, {
                      style: {
                        ...selectedTextElement.style,
                        border: {
                          ...selectedTextElement.style.border,
                          color: e.target.value
                        }
                      }
                    })}
                    className={styles.colorTextInput}
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>
            </>
          )}

          <div className={styles.buttonSection}>
            <button 
              className={styles.deleteButton}
              onClick={() => onRemoveTextElement(selectedTextElement.id)}
              title="Delete text element"
            >
              <FontAwesomeIcon icon={faTrash} />
              Delete
            </button>
          </div>
        </div>
      )}

      <TextTemplates 
        onApplyTemplate={(template) => {
          onAddTextElement(template.text, { x: 200, y: 200 }, {
            fontSize: template.style.fontSize,
            fontFamily: template.style.fontFamily,
            color: template.style.color,
            textAlign: template.style.textAlign || 'center',
            shadow: template.style.shadow,
            border: template.style.border
          });
        }}
      />
    </div>
  );

  const renderShapesTab = () => (
    <div className={styles.tabContent}>
      <h3>Shape Elements</h3>
      
      <div className={styles.addSection}>
        <div className={styles.shapeButtons}>
          <button onClick={() => handleAddShape('rectangle')} className={styles.shapeButton}>
            Rectangle
          </button>
          <button onClick={() => handleAddShape('circle')} className={styles.shapeButton}>
            Circle
          </button>
        </div>
      </div>

      {selectedShapeElement && (
        <div className={styles.selectedElement}>
          <h4>Selected Shape</h4>
          
          <div className={styles.controlRow}>
            <div className={styles.control}>
              <label>Width</label>
              <input
                type="number"
                value={selectedShapeElement.dimensions.width}
                onChange={(e) => onUpdateShapeElement(selectedShapeElement.id, {
                  dimensions: { ...selectedShapeElement.dimensions, width: Number(e.target.value) || 100 }
                })}
                min={10}
                max={800}
                className={styles.numberInput}
              />
            </div>
            <div className={styles.control}>
              <label>Height</label>
              <input
                type="number"
                value={selectedShapeElement.dimensions.height}
                onChange={(e) => onUpdateShapeElement(selectedShapeElement.id, {
                  dimensions: { ...selectedShapeElement.dimensions, height: Number(e.target.value) || 100 }
                })}
                min={10}
                max={450}
                className={styles.numberInput}
              />
            </div>
          </div>

          <div className={styles.control}>
            <label>Fill Color</label>
            <div className={styles.colorInputContainer}>
              <input
                type="color"
                value={selectedShapeElement.style.fillColor}
                onChange={(e) => onUpdateShapeElement(selectedShapeElement.id, {
                  style: { ...selectedShapeElement.style, fillColor: e.target.value }
                })}
                className={styles.colorInput}
              />
              <input
                type="text"
                value={selectedShapeElement.style.fillColor}
                onChange={(e) => onUpdateShapeElement(selectedShapeElement.id, {
                  style: { ...selectedShapeElement.style, fillColor: e.target.value }
                })}
                className={styles.colorTextInput}
                placeholder="#3b82f6"
              />
            </div>
          </div>

          <div className={styles.control}>
            <label>Fill Opacity: {selectedShapeElement.style.fillOpacity}%</label>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={selectedShapeElement.style.fillOpacity}
              onChange={(e) => onUpdateShapeElement(selectedShapeElement.id, {
                style: { ...selectedShapeElement.style, fillOpacity: parseInt(e.target.value) }
              })}
              className={styles.customSlider}
            />
          </div>

          <div className={styles.deleteSection}>
            <button 
              className={styles.deleteButton}
              onClick={() => onRemoveShapeElement(selectedShapeElement.id)}
              title="Delete shape element"
            >
              <FontAwesomeIcon icon={faTrash} />
              Delete Shape
            </button>
          </div>
        </div>
      )}

      {shapeElements.length > 0 && (
        <div className={styles.elementList}>
          <h4>{t('thumbnailEditor.shapesTab.allShapeElements')}</h4>
          {shapeElements.map(element => (
            <div key={element.id} className={styles.elementItem}>
              <span>{element.type}</span>
              <button 
                className={styles.deleteButton}
                onClick={() => onRemoveShapeElement(element.id)}
                title="Delete shape"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAssetsTab = () => {
    const selectedAssetElement = assetElements.find(el => el.id === selectedElement);

    return (
      <div className={styles.tabContent}>
        <div className={styles.control}>
          <label>{t('thumbnailEditor.assetsTab.uploadAssets')}</label>
          <input
            type="file"
            id="asset-upload"
            multiple
            accept="image/*"
            onChange={handleAssetUpload}
            style={{ display: 'none' }}
          />
          <button 
            onClick={() => document.getElementById('asset-upload')?.click()}
            className={`${styles.actionButton} ${styles.primary}`}
            disabled={isUploading}
          >
            <FontAwesomeIcon icon={faUpload} />
            {isUploading ? t('thumbnailEditor.assetsTab.uploading') : t('thumbnailEditor.assetsTab.uploadImages')}
          </button>
          <p style={{ fontSize: '0.8rem', color: '#888', margin: '0.5rem 0 0 0' }}>
            {t('thumbnailEditor.assetsTab.supportedFormats')}
          </p>
        </div>

        {isLoadingAssets && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
            Loading your assets...
          </div>
        )}

        {!isLoadingAssets && userAssets.length > 0 && (
          <div className={styles.assetLibrary}>
            <div className={styles.sectionTitle}>{t('thumbnailEditor.assetsTab.yourAssetLibrary')}</div>
            <div className={styles.assetGrid}>
              {userAssets.map(asset => (
                <div key={asset.id} className={styles.assetItem}>
                  <div className={styles.assetPreview}>
                    <img 
                      src={asset.assetUrl} 
                      alt={asset.originalName || asset.assetName}
                      className={styles.assetThumbnail}
                    />
                  </div>
                  <div className={styles.assetInfo}>
                    <span className={styles.assetName}>{asset.assetName || asset.originalName}</span>
                    <div className={styles.assetActions}>
                      <button
                        className={styles.addAssetButton}
                        onClick={() => handleAddAssetToCanvas(asset)}
                        title="Add a new instance of this asset to the canvas"
                      >
                        {t('thumbnailEditor.assetsTab.addToCanvas')}
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleRemoveAsset(asset.id)}
                        title="Delete this asset from your library (removes all instances from canvas)"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoadingAssets && userAssets.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
            No assets uploaded yet. Upload some images to get started!
          </div>
        )}

        {selectedAssetElement && (
          <div className={styles.selectedElement}>
            <div className={styles.sectionTitle}>Selected Asset</div>
            
            <div className={styles.controlRow}>
              <div className={styles.control}>
                <label>Width</label>
                <input
                  type="number"
                  value={selectedAssetElement.dimensions.width}
                  onChange={(e) => onUpdateAssetElement(selectedAssetElement.id, {
                    dimensions: { ...selectedAssetElement.dimensions, width: Number(e.target.value) || 50 }
                  })}
                  min={10}
                  max={800}
                  className={styles.numberInput}
                />
              </div>
              <div className={styles.control}>
                <label>Height</label>
                <input
                  type="number"
                  value={selectedAssetElement.dimensions.height}
                  onChange={(e) => onUpdateAssetElement(selectedAssetElement.id, {
                    dimensions: { ...selectedAssetElement.dimensions, height: Number(e.target.value) || 50 }
                  })}
                  min={10}
                  max={450}
                  className={styles.numberInput}
                />
              </div>
            </div>

            <div className={styles.control}>
              <label>Opacity: {selectedAssetElement.opacity}%</label>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={selectedAssetElement.opacity}
                onChange={(e) => onUpdateAssetElement(selectedAssetElement.id, {
                  opacity: parseInt(e.target.value)
                })}
                className={styles.customSlider}
              />
            </div>

            <div className={styles.control}>
              <label>Rotation: {selectedAssetElement.rotation || 0}°</label>
              <input
                type="range"
                min="0"
                max="360"
                step="1"
                value={selectedAssetElement.rotation || 0}
                onChange={(e) => onUpdateAssetElement(selectedAssetElement.id, {
                  rotation: parseInt(e.target.value)
                })}
                className={styles.customSlider}
              />
            </div>

            <div className={styles.buttonSection}>
              <button 
                className={styles.deleteButton}
                onClick={() => onRemoveAssetElement(selectedAssetElement.id)}
                title="Delete asset element"
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete Asset
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.rightPanel}>
      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === 'image' ? styles.active : ''}`}
          onClick={() => handleTabChange('image')}
        >
          <FontAwesomeIcon icon={faImage} />
          {t('thumbnailEditor.tabs.image')}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'text' ? styles.active : ''}`}
          onClick={() => handleTabChange('text')}
        >
          <FontAwesomeIcon icon={faFont} />
          {t('thumbnailEditor.tabs.text')}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'shapes' ? styles.active : ''}`}
          onClick={() => handleTabChange('shapes')}
        >
          <FontAwesomeIcon icon={faShapes} />
          {t('thumbnailEditor.tabs.shapes')}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'assets' ? styles.active : ''}`}
          onClick={() => handleTabChange('assets')}
        >
          <FontAwesomeIcon icon={faUpload} />
          {t('thumbnailEditor.tabs.assets')}
        </button>
      </div>

      <div className={styles.tabContainer}>
        {activeTab === 'image' && renderImageTab()}
        {activeTab === 'text' && renderTextTab()}
        {activeTab === 'shapes' && renderShapesTab()}
        {activeTab === 'assets' && renderAssetsTab()}
      </div>
    </div>
  );
};

export default ThumbnailRightPanel;